/* eslint-disable react/no-multi-comp, filenames/match-exported */
import React from 'react'
import classNames from 'classnames'
import { Paragraph } from '@scentregroup/ui'
import { ParsedOpeningHour } from '../../helpers/parse-opening-hours/types'
import { useEvolvedContext } from '../../context'
import style from './index.module.scss'

export enum themeTypes {
  fiftyFifty = 'fiftyFifty',
  leftAligned = 'leftAligned',
}
const themes = {
  [themeTypes.fiftyFifty]: style.fiftyFifty,
  [themeTypes.leftAligned]: style.leftAligned,
}

export interface TradingHoursLineProps {
  openHourDay: ParsedOpeningHour
  theme?: themeTypes
}

function hasNoHours(openHourDay: ParsedOpeningHour): boolean {
  return !openHourDay || !openHourDay.day || !openHourDay.hours
}

function hasNoDescription(text: ParsedOpeningHour['text']): boolean {
  return !text || !text.description
}

const DayParagraph = ({
  day,
}: {
  day: ParsedOpeningHour['day']
}): JSX.Element => (
  <Paragraph className={style.status} dataTestId="opening-hours-day">
    {day}
  </Paragraph>
)

const HoursParagraph = ({
  hours,
}: {
  hours: ParsedOpeningHour['hours']
}): JSX.Element =>
  hours.length > 1 ? (
    <span>
      {hours.map((hour: string) => (
        <Paragraph
          className={style.hours}
          dataTestId="opening-hours-hour"
          key={hour}
        >
          {hour}
        </Paragraph>
      ))}
    </span>
  ) : (
    <Paragraph className={style.hours} dataTestId="opening-hours-hour">
      {hours}
    </Paragraph>
  )

const DescriptionText = ({
  text,
}: {
  text: ParsedOpeningHour['text']
}): JSX.Element | null =>
  hasNoDescription(text) ? null : (
    <Paragraph
      className={
        text.isColored ? style.specialDescription : style.defaultDescription
      }
      dataTestId="opening-hours-desc"
    >
      {text.description}
    </Paragraph>
  )

const TradingHoursLine: React.FC<TradingHoursLineProps> = ({
  openHourDay,
  theme,
}): JSX.Element | null => {
  const isEvolved = useEvolvedContext()

  if (hasNoHours(openHourDay)) {
    return null
  }
  const { hours, day, text } = openHourDay
  const styles = classNames(style.TradingHoursLine, theme && themes[theme], {
    [style.active]: day === 'Today',
    [style.evolved]: isEvolved,
  })
  return (
    <div className={styles} data-test-id="opening-hours-entry">
      <DayParagraph day={day} />
      <HoursParagraph hours={hours} />
      <DescriptionText text={text} />
    </div>
  )
}

export default TradingHoursLine
